import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, Subscription, timer } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { APP_CONFIG } from '../../../config';
import { SecureStorageService } from '../../../core/services/secure-storage.service';
import { SessionEventService } from '../../../core/services/session-event.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/api:t3u1HoXb${APP_CONFIG.stagingName}`;
  private idleTimeout: any;
  private absoluteTimeout: any;
  private idleLimit = 30 * 60 * 1000; // 30 minutes
  private absoluteLimit = 60 * 60 * 1000; // 30 minutes
  // Window activity listeners are only ever attached once — they call
  // resetIdleTimer() for the lifetime of the tab, but that method now no-ops
  // once logged out, so re-adding them on every login isn't needed anyway.
  private activityMonitored = false;
  private isLoggingOut = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private secureStorage: SecureStorageService,
    private sessionEventService: SessionEventService
  ) { }

  getHeaders(): HttpHeaders {
    const token = this.secureStorage.getItem('authToken') || '';

    let headers = new HttpHeaders({
      Accept: '*/*',
    });

    headers = headers.set('x-data-source', environment.dataSource);
    headers = headers.set('x-data-source', environment.dataSource);

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else {
      console.warn('⚠️ No Auth Token Found in Local Storage!');
    }
    return headers;
  }

  /** Login API Call */
  login(loginData: any): Observable<{ user: any }> {
    const headers = new HttpHeaders({
      'X-Data-Source': environment.dataSource,
      'X-Skip-Loader': 'true',
    });

    return this.http
      .post<{ user: any }>(`${this.apiUrl}/auth/admin/login`, loginData, {
        headers,
      })
      .pipe(
        tap((response: any) => {
          const user = response.user;
          if (user?.authToken) {
            this.secureStorage.setItem('id', user.id.toString());
            this.secureStorage.setItem('authToken', user?.authToken);

            // Loyalty micro-service admin token (returned by Xano for super_admins only).
            // Used by LoyaltyService to call the points/loyalty gateway (8xxx / 5xxx).
            const msToken = response?.msAuthToken || user?.msAuthToken;
            if (msToken) {
              this.secureStorage.setItem('msAuthToken', msToken);
            }

            this.startSessionTimers();
          } else {
            throw new Error('Login failed: No auth token received.');
          }
        })
      );
  }

  /** Forgot Password API Call */
  forgotPassword(body: any): Observable<any> {
    let headers = this.getHeaders();
    headers = headers.set('X-Skip-Loader', 'true');

    const url = `${this.apiUrl}/admin_forgot_password`;
    return this.http.post(url, body, { headers });
  }

  resetPassword(body: any) {
    let headers = this.getHeaders();
    const url = `${this.apiUrl}/reset-password`;
    return this.http.post(url, body, { headers: headers });
  }

  /** Logout */
  logout(): void {
    // Already logged out (or a logout is already in flight) — nothing to do.
    // Without this guard, a stray idle/absolute timer firing after the token
    // is cleared would POST to the logout endpoint with no valid token, get a
    // 401 back, and the auth interceptor would call logout() again for that
    // 401 — an infinite logout↔401 loop.
    const token = this.secureStorage.getItem('authToken');
    if (this.isLoggingOut || !token) {
      this.finishLogout();
      return;
    }
    this.isLoggingOut = true;

    // Call the backend logout API (needs the auth header, so fire it before
    // clearing the token). The backend records the "logout" action in the
    // super admin and activity logs. Redirect regardless of success/failure.
    let headers = this.getHeaders();
    headers = headers.set('X-Skip-Loader', 'true');
    const body = { id: this.secureStorage.getItem('id') || '' };

    this.http
      .post(`${this.apiUrl}/auth/admin/logout`, body, { headers })
      .subscribe({
        next: () => this.finishLogout(),
        error: () => this.finishLogout(),
      });
  }

  /** Clear session data and redirect to login. */
  private finishLogout(): void {
    this.secureStorage.removeItem('authToken');
    this.secureStorage.removeItem('msAuthToken');
    this.secureStorage.removeItem('id');
    this.clearTimers();
    this.isLoggingOut = false;
    this.router.navigate(['/auth/login']);
  }

  /** Re-arm the idle/absolute session timers for an already-authenticated
   * session (e.g. on a hard page refresh where login() never re-runs). */
  initSessionIfAuthenticated(): void {
    const token = this.secureStorage.getItem('authToken');
    if (token) {
      this.startSessionTimers();
    }
  }

  /** Start session timers */
  private startSessionTimers(): void {
    this.clearTimers();

    // Absolute timeout disabled — backend already enforces token-based
    // session expiry, so this was a redundant hard cutoff. Uncomment to
    // re-enable a frontend-forced absolute logout after `absoluteLimit`.
    // this.absoluteTimeout = setTimeout(() => {
    //   this.sessionEventService.notifySessionTimeout();
    //   this.logout();
    //   this.toastr.success('Session expired after 30 minutes.');
    // }, this.absoluteLimit);

    // Idle timeout handling
    this.resetIdleTimer();
    this.monitorUserActivity();
  }

  /** Reset idle timer on activity */
  private resetIdleTimer(): void {
    if (this.idleTimeout) clearTimeout(this.idleTimeout);

    // Activity on the login page (or after any logout) must not re-arm this —
    // otherwise the leftover window listeners keep resetting a timer for a
    // session that no longer exists, and it eventually fires logout() with no
    // valid token (401 → interceptor calls logout() again → infinite loop).
    if (!this.secureStorage.getItem('authToken')) return;

    this.idleTimeout = setTimeout(() => {
      this.sessionEventService.notifySessionTimeout();
      this.logout();
      this.toastr.success('Logged out due to 30 minutes of inactivity.');
    }, this.idleLimit);
  }

  /** Watch for user activity — attached once for the tab's lifetime. */
  private monitorUserActivity(): void {
    if (this.activityMonitored) return;
    this.activityMonitored = true;
    ['click', 'mousemove', 'keydown', 'scroll', 'touchstart'].forEach((event) =>
      window.addEventListener(event, () => this.resetIdleTimer())
    );
  }

  /** Clear timers */
  private clearTimers(): void {
    if (this.idleTimeout) clearTimeout(this.idleTimeout);
    if (this.absoluteTimeout) clearTimeout(this.absoluteTimeout);
  }
}
